import React, { useEffect, useState, useRef } from 'react';
import { Stepper } from 'react-form-stepper';
import useFetch from './components/useFetch';
import useForm from './components/useForm';
import useSubmit from './components/useSubmit';
import CalculatorForm from './components/CalculatorForm';
import OrderForm from './components/OrderForm';
import OrderCompleteForm from './components/OrderCompleteForm';
import validate from './components/FormValidationRules';
import './App.css';

function App() {
  const lang = document.getElementById('root').getAttribute('lang') || 'sv';
  const [activeStep, setActiveStep] = useState(0);
  const [submitClick, setSubmitClick] = useState(false);
  const [translations, fetching] = useFetch(`/${lang}/api/v1/languages?_format=json`);
  const [data, loading] = useFetch(`/${lang}/api/v1/calculator?_format=json`);
  const formEl = useRef(null);
  const captchaRef = useRef(null);
  const steps = [{ label: !fetching ? translations[0].i18n.steps.step_1 : '' }, { label: !fetching ? translations[0].i18n.steps.step_2 : '' }, { label: !fetching ? translations[0].i18n.steps.step_3 : '' }];

  const initialValues = {
    landskap: '',
    min_order: 400,
    kvm: '',
    antal_plan: '',
    plan_tillagg: 0,
    lutning: '',
    lutnings_tillagg: 0,
    farg: '',
    farg_tillagg: 0,
    tegelpanna_byte: false,
    tegelpanna_pris: 0,
    tegelpanna_antal: '',
    tegelpanna_lager: false,
    tegelpanna_tillagg: 0,
    undertak: false,
    pris_min: '',
    pris: '',
    total: '',
    moms: '',
    total_moms: '',
    namn: '',
    epost: '',
    adress: '',
    ort: '',
    telefon: '',
    postnr: '',
    terms: false,
    gdpr: false,
    recaptcha: null,
  };

  const {
    values, setValues, errors, handleChange, handleSubmit,
  } = useForm(
    initialValues,
    submitValues,
    validate,
    activeStep,
    translations,
    fetching,
  );

  const { min } = Math;
  const sqmPrice = (sqm, minSqm, maxSqm, minPrice = values.pris_min, maxPrice = values.pris) =>
  maxPrice - (maxPrice - minPrice)*((min(sqm, maxSqm) - minSqm) / (maxSqm - minSqm));
  const kvm4Landskap = x => sqmPrice(x, values.min_order, 4000);
  const addPct = pct => (pct / 100) + 1;
  const kvmTotal = ((kvm4Landskap(values.kvm) * values.kvm) * addPct(values.plan_tillagg) * addPct(values.lutnings_tillagg) * addPct(values.farg_tillagg)) + values.tegelpanna_antal*(values.tegelpanna_pris + values.tegelpanna_tillagg);
  const kvmTotalMoms = kvmTotal * 1.25;
  const moms = (1 - (1 / 1.25)) * kvmTotalMoms;

  useEffect(() => {
    if(!loading) {
      const pricing = data.filter(item => item.vid === 'kvadratmeterpris');
      setValues({
        ...values, pris_min: pricing[0].field_pris_min, pris: pricing[0].field_pris,
      });
    }
    // eslint-disable-next-line
  }, [loading, data]);

  const verify = () => {
    captchaRef.current.getResponse().then(res => {
      setValues({
        ...values, recaptcha: res
      });
    })
  };

  const [submitStatus] = useSubmit('/webform_rest/submit', submitClick, values, fetching, translations);

  const props = { values, errors, handleChange, fetching, translations, data, loading, submitStatus, captchaRef, verify };

  function submitValues() {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
    setValues({
      ...values, total: kvmTotal ? kvmTotal.toFixed(2) : '', moms: moms ? moms.toFixed(2) : '', total_moms: kvmTotalMoms ? kvmTotalMoms.toFixed(2) : '',
    });
    if (activeStep === 1) {
      setSubmitClick(true);
    }
  }

  function getStepContent(stepIndex) {
    switch (stepIndex) {
      case 0:
        return <CalculatorForm {...props} />;
      case 1:
        return <OrderForm {...props} />;
      case 2:
        return <OrderCompleteForm {...props} />;
      default:
        return <CalculatorForm {...props} />;
    }
  }

  function handleNext() {
    formEl.current.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  function handleBack() {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  }

  return (
    <div className="app">
      <div className="tw-container tw-max-w-screen-sm tw-mx-auto tw-pt-40">
        <h1 className="tw-text-4xl tw-mb-5">{!fetching && translations[0].i18n.page.title}</h1>
        <Stepper
          steps={steps}
          activeStep={activeStep}
          styleConfig={{ activeBgColor: '#ff7200', completedBgColor: '#fdc236' }}
        />
      </div>
      <div className="tw-preflight">
        <div className="tw-container tw-max-w-screen-sm tw-mx-auto tw-pb-40">
          <form onSubmit={handleSubmit} noValidate ref={formEl} id="calculator-form">
            {getStepContent(activeStep)}
            {(values.min_order && values.kvm && values.antal_plan && values.lutning && values.farg && (values.plan_tillagg !== 100)) && 
            <div className="tw-my-5">
              <table className="tw-table-auto tw-w-full tw-text-xl">
                <tr>
                  <td>
                    {translations[0].i18n.order.price}
                  </td>
                  <td className="tw-text-right">
                    {kvmTotal.toFixed(2)} SEK
                  </td>
                </tr>
                <tr>
                  <td>
                    {translations[0].i18n.order.vat}
                  </td>
                  <td className="tw-text-right">
                    {moms.toFixed(2)} SEK
                  </td>
                </tr>
                <tr>
                  <td className="tw-font-semibold">
                    {translations[0].i18n.order.total}
                  </td>
                  <td className="tw-text-right tw-font-semibold">
                    {kvmTotalMoms.toFixed(2)} SEK
                  </td>
                </tr>
              </table>
            </div>}
            <div className="tw-flex tw-justify-between">
              <button 
                disabled={activeStep === 0 || activeStep === 2}
                onClick={handleBack}
                className={`${(activeStep === 0 || activeStep === 2) ? 'tw-bg-gray-200' : 'tw-bg-gray-900'} hover:tw-bg-gray-700 tw-text-white tw-font-bold tw-py-2 tw-px-4`}
              >
                {!fetching && translations[0].i18n.order.back}
              </button>
              <button 
                disabled={activeStep === 2 || values.plan_tillagg === 100}
                onClick={handleNext}
                className={`${activeStep === 2 || values.plan_tillagg === 100 ? 'tw-bg-gray-200' : 'tw-bg-gray-900'} hover:tw-bg-gray-700 tw-text-white tw-font-bold tw-py-2 tw-px-4`}
              >
                {!fetching && (activeStep === 1 ? translations[0].i18n.order.submit : translations[0].i18n.order.next)}
              </button>
            </div>
          </form>
          {values.plan_tillagg === 100 && <h3 className="tw-mt-5 tw-text-3xl font-semibold tw-normal-case">{!fetching && translations[0].i18n.contact.title}</h3>}
        </div>
      </div>
    </div>
  );
}

export default App;
